import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, ChevronLeft, ChevronRight, BedDouble, KeyRound, Save, Undo2, Shuffle } from "lucide-react";
import { toast } from "sonner";
import { parseLocalDate, toLocalIso, addDaysIso } from "@/lib/dateUtils";

const VIEW_LENGTHS = { day: 1, week: 7, month: 31, quarter: 92 };
const STATUS_COLOR = {
  draft: "bg-gray-300 text-gray-900",
  confirmed: "bg-emerald-500 text-white",
  checked_in: "bg-blue-500 text-white",
  completed: "bg-[#1B4332] text-white",
};

// Use local-date formatting so day-keys match displayed weekday names.
function fmtDate(d) { return toLocalIso(d); }

export default function HousingCalendar() {
  const navigate = useNavigate();
  const [view, setView] = useState("month"); // day / week / month / quarter
  const [start, setStart] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });
  const [grid, setGrid] = useState({ units: [], bookings_by_unit: {}, leases_by_unit: {} });
  const [loading, setLoading] = useState(true);
  // Phase 56b — buffered drag-and-drop moves. Keyed by `${booking_id}|${segment_id}`
  // → { unit_id, from_date, to_date }. Optimistic UI re-derives from `grid` + this.
  const [pendingMoves, setPendingMoves] = useState({});
  const [saving, setSaving] = useState(false);
  const pendingCount = Object.keys(pendingMoves).length;

  const days = useMemo(() => {
    const len = VIEW_LENGTHS[view];
    return Array.from({ length: len }, (_, i) => {
      const d = new Date(start); d.setDate(d.getDate() + i); return d;
    });
  }, [start, view]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fromDate = fmtDate(start);
      const end = new Date(start); end.setDate(end.getDate() + VIEW_LENGTHS[view]);
      const toDate = fmtDate(end);
      const { data } = await api.get(`/bookings/calendar/grid?from_date=${fromDate}&to_date=${toDate}`);
      // Backwards-compat: if backend hasn't shipped leases_by_unit, treat as empty.
      setGrid({ leases_by_unit: {}, ...data });
      setPendingMoves({});
    } catch { toast.error("Failed to load calendar"); }
    finally { setLoading(false); }
  }, [start, view]);

  useEffect(() => { load(); }, [load]);

  // Phase 56b — overlay pending moves so the grid reflects drags immediately.
  // Builds a fresh `bookings_by_unit` per render: walks every original segment,
  // applies any pending move, and re-buckets under the new unit.
  const displayGrid = useMemo(() => {
    if (pendingCount === 0) return grid;
    const next = { ...grid, bookings_by_unit: {} };
    Object.entries(grid.bookings_by_unit || {}).forEach(([unitId, segs]) => {
      segs.forEach((s) => {
        const k = `${s.booking_id}|${s.segment_id || `${unitId}-${s.from_date}`}`;
        const m = pendingMoves[k];
        const target = m ? { ...s, ...m, _pending: true } : s;
        const targetUnit = (m && m.unit_id) || unitId;
        next.bookings_by_unit[targetUnit] = next.bookings_by_unit[targetUnit] || [];
        next.bookings_by_unit[targetUnit].push(target);
      });
    });
    return next;
  }, [grid, pendingMoves, pendingCount]);

  // Drag-and-drop: drop a segment on any (unit, day) cell to shift it.
  const onSegDragStart = (e, seg, unitId) => {
    e.dataTransfer.setData("application/x-booking-seg", JSON.stringify({
      booking_id: seg.booking_id,
      segment_id: seg.segment_id || `${unitId}-${seg.from_date}`,
      from_date: seg.from_date,
      to_date: seg.to_date,
      unit_id: unitId,
    }));
    e.dataTransfer.effectAllowed = "move";
  };
  const onCellDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onCellDrop = (e, targetUnitId, targetDayStr) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("application/x-booking-seg");
    if (!raw) return;
    let payload;
    try { payload = JSON.parse(raw); } catch { return; }
    const stayDays = (parseLocalDate(payload.to_date) - parseLocalDate(payload.from_date)) / 86400000;
    const newFrom = targetDayStr;
    const newTo = addDaysIso(newFrom, Math.max(1, Math.round(stayDays)));
    if (newFrom === payload.from_date && newTo === payload.to_date && targetUnitId === payload.unit_id) return;
    const k = `${payload.booking_id}|${payload.segment_id}`;
    setPendingMoves((prev) => ({
      ...prev,
      [k]: { unit_id: targetUnitId, from_date: newFrom, to_date: newTo },
    }));
    toast.info(`Moved to ${newFrom} → ${newTo} (unsaved)`);
  };

  const discardPending = () => {
    if (pendingCount === 0) return;
    if (!window.confirm(`Discard ${pendingCount} unsaved move${pendingCount === 1 ? "" : "s"}?`)) return;
    setPendingMoves({});
    toast.info("Pending moves discarded");
  };

  const savePending = async () => {
    if (pendingCount === 0) return;
    setSaving(true);
    let ok = 0, fail = 0;
    // Each move = update the segment's unit/dates on its parent booking.
    await Promise.all(Object.entries(pendingMoves).map(async ([k, patch]) => {
      const [bookingId, segmentId] = k.split("|");
      try {
        const { data: b } = await api.get(`/bookings/${bookingId}`);
        const segs = (b.segments || []).map((s) =>
          (s.id === segmentId || `${s.unit_id}-${s.from_date}` === segmentId)
            ? { ...s, unit_id: patch.unit_id, from_date: patch.from_date, to_date: patch.to_date }
            : s,
        );
        const stayFrom = segs.reduce((m, s) => (s.from_date < m ? s.from_date : m), segs[0].from_date);
        const stayTo = segs.reduce((m, s) => (s.to_date > m ? s.to_date : m), segs[0].to_date);
        await api.put(`/bookings/${bookingId}`, {
          ...b, segments: segs, stay_from: stayFrom, stay_to: stayTo,
        });
        ok++;
      } catch { fail++; }
    }));
    if (fail === 0) toast.success(`Saved ${ok} move${ok === 1 ? "" : "s"}`);
    else toast.error(`${ok} saved · ${fail} failed — reloading`);
    setSaving(false);
    load();
  };

  // beforeunload guard
  useEffect(() => {
    if (pendingCount === 0) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [pendingCount]);

  const shiftDays = (n) => {
    const d = new Date(start); d.setDate(d.getDate() + n); setStart(d);
  };
  const goToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); setStart(d); };

  const headerLabel = useMemo(() => {
    if (view === "day") return start.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    if (view === "week") {
      const end = new Date(start); end.setDate(end.getDate() + 6);
      return `${start.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
    }
    if (view === "quarter") {
      const end = new Date(start); end.setDate(end.getDate() + 91);
      return `${start.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} – ${end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
    }
    return start.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }, [start, view]);

  // Helper: clamp a [start, end) range into the visible window and return
  // {leftPct, widthPct, openLeft, openRight} for absolute positioning over the day cells.
  const windowFrom = fmtDate(days[0]);
  const windowToD = new Date(days[days.length - 1]); windowToD.setDate(windowToD.getDate() + 1);
  const windowTo = fmtDate(windowToD);
  const totalDays = days.length;

  const leasePosition = (lease) => {
    const lsStart = lease.start_date;
    const lsEnd = lease.end_date || "2125-01-01";
    if (lsEnd <= windowFrom || lsStart >= windowTo) return null;
    const clampStart = lsStart < windowFrom ? windowFrom : lsStart;
    const clampEnd = lsEnd > windowTo ? windowTo : lsEnd;
    // Day index of clampStart vs windowFrom.
    const startIdx = (parseLocalDate(clampStart) - parseLocalDate(windowFrom)) / 86400000;
    const endIdx = (parseLocalDate(clampEnd) - parseLocalDate(windowFrom)) / 86400000;
    const leftPct = (startIdx / totalDays) * 100;
    const widthPct = ((endIdx - startIdx) / totalDays) * 100;
    return {
      leftPct, widthPct,
      openLeft: lsStart < windowFrom,
      openRight: lease.open_ended || lsEnd > windowTo,
    };
  };

  const totalLeaseCount = useMemo(
    () => Object.values(displayGrid.leases_by_unit || {}).reduce((s, arr) => s + arr.length, 0),
    [displayGrid],
  );

  // Phase 56b — detect lease/booking clashes per unit so we can paint both bars
  // red. A clash is any lease whose [start_date, end_date) overlaps any booking
  // segment [from_date, to_date) on the same unit. Open-ended leases use a
  // far-future sentinel. Shared-unit leases are exempt (intentional co-use).
  const conflictsByUnit = useMemo(() => {
    const out = {}; // unit_id → { leases: Set<lease_id>, bookings: Set<booking_id|segment_id> }
    const FAR = "2125-01-01";
    Object.entries(displayGrid.leases_by_unit || {}).forEach(([uid, leases]) => {
      const segs = displayGrid.bookings_by_unit?.[uid] || [];
      if (!leases.length || !segs.length) return;
      leases.forEach((ls) => {
        if (ls.shared_unit) return;
        const lsEnd = ls.end_date || FAR;
        segs.forEach((seg) => {
          if (seg.from_date < lsEnd && seg.to_date > ls.start_date) {
            out[uid] = out[uid] || { leases: new Set(), bookings: new Set() };
            out[uid].leases.add(ls.lease_id);
            out[uid].bookings.add(`${seg.booking_id}|${seg.segment_id || `${uid}-${seg.from_date}`}`);
          }
        });
      });
    });
    return out;
  }, [displayGrid]);
  const conflictCount = useMemo(
    () => Object.values(conflictsByUnit).reduce((s, c) => s + c.leases.size, 0),
    [conflictsByUnit],
  );

  return (
    <div className="space-y-6 fade-in" data-testid="housing-calendar-page">
      {/* Phase 56b — sticky save bar for drag-and-drop moves */}
      {pendingCount > 0 && (
        <div className="sticky top-0 z-40 -mx-4 md:-mx-8 lg:-mx-12 px-4 md:px-8 lg:px-12 py-3 bg-amber-50 border-y border-amber-300 flex items-center justify-between gap-3 flex-wrap" data-testid="housing-save-bar">
          <div className="flex items-center gap-2 text-sm text-amber-900">
            <Save className="h-4 w-4" />
            <span><strong data-testid="housing-pending-count">{pendingCount}</strong> unsaved move{pendingCount === 1 ? "" : "s"}</span>
            <span className="text-xs text-amber-800/80 hidden sm:inline">— drag bookings to new dates or units, then save when ready.</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={discardPending} disabled={saving} className="border-amber-300 text-amber-900 hover:bg-amber-100" data-testid="housing-discard-btn">
              <Undo2 className="h-3.5 w-3.5 mr-1" /> Discard
            </Button>
            <Button size="sm" onClick={savePending} disabled={saving} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="housing-save-btn">
              <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving…" : `Save ${pendingCount} move${pendingCount === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      )}
      <Button variant="ghost" onClick={() => navigate("/bookings")} className="text-[#525C58]">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Bookings
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Housing Calendar</h1>
          <p className="text-[#525C58] mt-1">Hotel-style grid showing every unit's bookings <span className="text-purple-700 font-medium">and active leases</span>.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/scramble")}
            className="border-amber-400 text-amber-800 hover:bg-amber-50"
            data-testid="scramble-btn-calendar"
          >
            <Shuffle className="h-4 w-4 mr-1" /> Scramble
          </Button>
          {Object.keys(VIEW_LENGTHS).map((v) => (
            <Button
              key={v}
              variant={view === v ? "default" : "outline"}
              size="sm"
              onClick={() => setView(v)}
              className={view === v ? "bg-[#1B4332]" : "border-[#1B4332]/20 text-[#1B4332]"}
              data-testid={`view-${v}-btn`}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      <Card className="border-[#E8E8E6]">
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-4 border-b border-[#E8E8E6] flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => shiftDays(-VIEW_LENGTHS[view])} className="border-[#E8E8E6]" data-testid="prev-btn">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={goToday} className="border-[#E8E8E6]">Today</Button>
              <Button size="sm" variant="outline" onClick={() => shiftDays(VIEW_LENGTHS[view])} className="border-[#E8E8E6]" data-testid="next-btn">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <h2 className="text-lg font-semibold text-[#1B4332]" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="cal-header-label">
              {headerLabel}
            </h2>
            <div className="flex items-center gap-3 text-xs flex-wrap">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500" /> Confirmed</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500" /> Checked in</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-300" /> Draft</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-purple-500" /> Lease</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-600" /> Clash</span>
              {totalLeaseCount > 0 && (
                <span className="ml-2 text-[#7A8A84] hidden md:inline">{totalLeaseCount} lease{totalLeaseCount === 1 ? "" : "s"} in window</span>
              )}
              {conflictCount > 0 && (
                <span className="ml-2 text-red-700 font-semibold" data-testid="conflict-count">
                  ⚠ {conflictCount} clash{conflictCount === 1 ? "" : "es"}
                </span>
              )}
            </div>
          </div>

          {loading ? (
            <div className="py-20 text-center text-[#7A8A84]">Loading…</div>
          ) : grid.units.length === 0 ? (
            <div className="py-20 text-center text-[#7A8A84]">
              <BedDouble className="h-8 w-8 mx-auto mb-2 opacity-40" />
              No active housing units. Add some first.
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="calendar-grid">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="bg-[#F4F4F1] border-b border-[#E8E8E6]">
                    <th className="sticky left-0 bg-[#F4F4F1] z-10 w-44 p-3 text-left font-semibold text-[#1C1F1E] border-r border-[#E8E8E6]">
                      Unit
                    </th>
                    {days.map((d) => {
                      const isToday = fmtDate(d) === fmtDate(new Date());
                      return (
                        <th key={d.toISOString()} className={`p-2 font-medium border-r border-[#E8E8E6] min-w-[40px] ${isToday ? "bg-[#E9F5DB] text-[#1B4332]" : "text-[#7A8A84]"}`}>
                          <div className="text-[10px] uppercase">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                          <div className="text-sm">{d.getDate()}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {displayGrid.units.map((u) => {
                    const unitLeases = (displayGrid.leases_by_unit?.[u.id] || []);
                    const unitConflicts = conflictsByUnit[u.id];
                    return (
                      <tr key={u.id} className="border-b border-[#E8E8E6] hover:bg-[#F4F4F1]/40" data-testid={`row-${u.id}`}>
                        <td className="sticky left-0 bg-white z-10 p-3 border-r border-[#E8E8E6]">
                          <p className="font-medium text-[#1C1F1E] text-sm">{u.name}</p>
                          <p className="text-[10px] text-[#7A8A84]">cap {u.capacity}</p>
                          {unitLeases.length > 0 && (
                            <p className="text-[10px] text-purple-700 mt-1 flex items-center gap-1">
                              <KeyRound className="h-2.5 w-2.5" /> leased
                            </p>
                          )}
                        </td>

                        {/* The cells row + an absolutely-positioned lease band overlaid on top */}
                        <td colSpan={days.length} className="p-0 align-top relative h-16">
                          <div className="grid h-full" style={{ gridTemplateColumns: `repeat(${days.length}, minmax(40px, 1fr))` }}>
                            {days.map((d) => {
                              const dStr = fmtDate(d);
                              const seg = (displayGrid.bookings_by_unit[u.id] || []).find(b => dStr >= b.from_date && dStr < b.to_date);
                              const segKey = seg ? `${seg.booking_id}|${seg.segment_id || `${u.id}-${seg.from_date}`}` : null;
                              const segClashes = !!(seg && unitConflicts && unitConflicts.bookings.has(segKey));
                              const isToday = dStr === fmtDate(new Date());
                              return (
                                <div
                                  key={d.toISOString()}
                                  className={`border-r border-[#E8E8E6] relative ${isToday ? "bg-[#E9F5DB]/30" : ""}`}
                                  onDragOver={onCellDragOver}
                                  onDrop={(e) => onCellDrop(e, u.id, dStr)}
                                  data-testid={`drop-${u.id}-${dStr}`}
                                >
                                  {seg && dStr === seg.from_date && (
                                    <div
                                      draggable
                                      onDragStart={(e) => onSegDragStart(e, seg, u.id)}
                                      title={`${seg.visitor_name} · ${seg.from_date} → ${seg.to_date} · ${seg.occupancy} pax`}
                                      className={`absolute top-1 left-0.5 rounded text-[10px] px-1 py-0.5 cursor-grab active:cursor-grabbing truncate ${segClashes ? "bg-red-600 text-white ring-2 ring-red-700" : (STATUS_COLOR[seg.status] || "bg-gray-200")} ${seg._pending ? "ring-2 ring-amber-400" : ""}`}
                                      style={{
                                        zIndex: 2,
                                        // Stretch across the full stay so coordinators see the whole bar
                                        width: `calc(${(parseLocalDate(seg.to_date) - parseLocalDate(seg.from_date)) / 86400000} * 100% - 4px)`,
                                      }}
                                      onClick={() => navigate(`/bookings?edit=${seg.booking_id}`, { state: { from: "/housing-calendar" } })}
                                      data-testid={`cell-${u.id}-${dStr}`}
                                      data-pending={seg._pending ? "1" : undefined}
                                      data-clash={segClashes ? "1" : undefined}
                                    >
                                      {segClashes && <span className="mr-0.5">⚠</span>}{seg.visitor_name}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          {/* Lease bands — absolutely positioned across the whole row */}
                          {unitLeases.map((ls) => {
                            const pos = leasePosition(ls);
                            if (!pos) return null;
                            const isPending = ls.status === "pending";
                            const clashes = !!(unitConflicts && unitConflicts.leases.has(ls.lease_id));
                            const bg = clashes ? "bg-red-600" : (isPending ? "bg-purple-200" : "bg-purple-500");
                            const text = clashes ? "text-white" : (isPending ? "text-purple-900" : "text-white");
                            const tooltip = `${ls.tenant_name}${ls.tenant_phone ? " · " + ls.tenant_phone : ""}\nLease ${ls.start_date} → ${ls.open_ended ? "open-ended" : ls.end_date}\nKES ${Number(ls.monthly_rent || 0).toLocaleString()} / month` + (ls.shared_unit ? "\n(Shared unit override)" : "") + (clashes ? "\n⚠ CLASHES WITH A BOOKING" : "");
                            return (
                              <div
                                key={ls.lease_id}
                                title={tooltip}
                                onClick={() => navigate("/leases")}
                                data-testid={`lease-band-${ls.lease_id}`}
                                data-clash={clashes ? "1" : undefined}
                                className={`absolute bottom-1 ${bg} ${text} text-[10px] font-medium flex items-center gap-1 cursor-pointer hover:brightness-110 transition-all overflow-hidden ${clashes ? "ring-2 ring-red-700 animate-pulse" : ""}`}
                                style={{
                                  left: `${pos.leftPct}%`,
                                  width: `${pos.widthPct}%`,
                                  height: "20px",
                                  borderRadius: 4,
                                  paddingLeft: pos.openLeft ? 0 : 6,
                                  paddingRight: pos.openRight ? 0 : 6,
                                  // Visual cues for "extends past window":
                                  borderLeft: pos.openLeft ? "none" : "0",
                                  borderRight: pos.openRight ? "none" : "0",
                                  zIndex: 1,
                                }}
                              >
                                {pos.openLeft && (
                                  <span className="h-full px-1 text-[#7B5BC8] bg-purple-100 flex items-center" title="Started before window">‹</span>
                                )}
                                <span className="truncate">
                                  {clashes ? <span className="mr-0.5">⚠</span> : <KeyRound className="h-2.5 w-2.5 inline-block mr-0.5 mb-0.5" />}
                                  {ls.tenant_name}
                                  {pos.openRight && ls.open_ended && <span className="ml-1 opacity-90">· open-ended</span>}
                                </span>
                                {pos.openRight && !ls.open_ended && (
                                  <span className="ml-auto h-full px-1 text-[#7B5BC8] bg-purple-100 flex items-center" title="Continues past window">›</span>
                                )}
                                {pos.openRight && ls.open_ended && (
                                  <span className="ml-auto h-full px-1 text-[#7B5BC8] bg-purple-100 flex items-center" title="Open-ended">∞</span>
                                )}
                              </div>
                            );
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
